import { getAnalyticsScansMap } from 'store/analytics/actions'
import type { GetAnalyticsScansMapDataResponse, GetAnalyticsScansMapPayload } from 'store/analytics/types'
import { analyticsScansMapKey } from 'store/analytics/types'
import { useSelector } from 'store/hooks'

import type { UseQuerySelectOptions, UseQuerySelectResult } from 'hooks'

import { useQuerySelect } from 'hooks'

import {
  useAnalyticsAreaParam,
  useAnalyticsScanStatusParam,
  withAnalyticsArea,
  withAnalyticsScanStatuses,
} from './useAnalyticsAreaParam'


type UseAnalyticsParams = GetAnalyticsScansMapPayload

type UseAnalyticsResult = {
  data: GetAnalyticsScansMapDataResponse | null
} & UseQuerySelectResult<UseAnalyticsParams>

/**
 * ⚠️ READS THE ENTRY FOR ITS OWN REQUEST, not "the scans-map response".
 *
 * Every other analytics hook here can select its slot directly, because each has exactly
 * one consumer. This one does not: the page can hold the legacy map card plus any number
 * of per-metric map variants (VRG-467), and a certificates card asks a genuinely different
 * question (`mode: 'latest'`) from a scans one (`mode: 'all'`).
 *
 * Selecting the whole slot handed every card whichever response arrived last — see
 * `AnalyticsScansMapCache` in `store/analytics/types` for the observed failure and why
 * `takeLatest` made it worse rather than merely racy. Keying on the request means a card
 * reads its OWN answer or nothing, and `nothing` is the honest pre-arrival state that
 * `ScansByLocation`'s first-load skeleton already handles.
 */
export const useAnalyticsScansMap = (
  params: UseAnalyticsParams,
  options?: UseQuerySelectOptions
): UseAnalyticsResult => {
  /*
    The page-global drawn area (VRG-480), merged in HERE rather than at the call site.
    See `useAnalyticsAreaParam` — the merge is inside the hook so a new analytics card
    cannot forget it, and so `useQuerySelect`'s params comparison sees the ring and
    refetches when it changes.
  */
  const areaParam = useAnalyticsAreaParam()
  /*
    The page-level SCAN OUTCOME filter (VERA-260), merged in HERE for the same reason and
    by the same mechanism as `area` — see `useAnalyticsScanStatusParam`, including why this
    one is injected into THIS hook alone rather than into every analytics hook.

    Order is fixed — scan statuses first, then area — because `analyticsScansMapKey` is
    `JSON.stringify` over the result and property order is load-bearing to it.
  */
  const scanStatusParam = useAnalyticsScanStatusParam()
  const request = withAnalyticsArea(withAnalyticsScanStatuses(params, scanStatusParam), areaParam)

  const result = useQuerySelect(getAnalyticsScansMap, request, options)
  // The SAME object `useQuerySelect` dispatched, so this key and the saga's are identical
  // by construction (see `analyticsScansMapKey`). Not memoised: callers build `params`
  // inline, so a `[params]` dependency changes every render and the memo would never
  // hold — `useQuerySelect` already absorbs that instability with its own value compare.
  //
  // ⚠️ `request`, NOT `params`. The dispatched payload carries the drawn area, so keying
  // on the pre-merge object would read the entry written for a DIFFERENT ring — the map
  // would keep plotting the previous area's pins while every other card had moved on.
  const key = analyticsScansMapKey(request)
  const data = useSelector(state => state.analytics.scansMap[key] ?? null)

  return {
    data,
    ...result,
  }
}
